import {
  hasDirectives,
  checkDocument,
  removeDirectivesFromDocument
} from '@apollo/client/utilities';
import { visit } from 'graphql';
import { print } from 'graphql/language/printer';
import { ApolloLink, gql } from '@apollo/client';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';

const LOG = LoggerFactory.create('AppFeatureDirectiveLink');

/**
 * Set the value of a nexted graphql field in a result.
 */
const setField = (obj, field, value) => {
  if (!obj) {
    // If obj is not defined, that means that the shape of the query result is
    // missing the parent of the field with the directive. So return early.
    // Note: This could also indicate an expected graphql result or an unhandled
    // scenario. So don't entirely ignore this during development.
    LOG.debug(
      `Object is missing for key "${field[0]}", could not set default value`
    );
    return;
  }

  if (Array.isArray(obj)) {
    obj.forEach(element => setField(element, field, value));
  } else if (field.length === 1) {
    // eslint-disable-next-line no-param-reassign
    obj[field[0]] = value;
  } else {
    setField(obj[field[0]], field.slice(1), value);
  }
};

/**
 * @deprecated
 * This directive is deprecated in favor of using the built-in @include and @skip directives
 *
 * This directive allows fields in gql queries to only be sent to the backend
 * if the specified appFeature is enabled. This directive took inspiration from
 * https://github.com/svengau/apollo-link-directive and https://github.com/apollographql/apollo-link-rest.
 *
 * Here is an example that will only send the documents field to the server when
 * the documents appFeature is enabled. When the appFeatture is disabled documents
 * will be returned in the query as null.
 *
 *  sessionDetail(id: $id, qaLimit: $qaLimit) {
 *  id
 *   name
 *   documents @appFeature(feature: "documents") {
 *     id
 *     name
 *     uri
 *     size
 *     extension
 *   }
 * }
 *
 * Limitations:
 * - Nested directives are not fully tested or supported, as the first directive
 * that is off will insert null and other directives will likely be ignored.
 * - Only null can currently be returned for a field that is off. If a different
 * value is needed it would be a fairly simple enhancement to add.
 * - For this to be useful, the lambda resolvers need to observe fields in the query.
 * And due to limitations in AWS that means that fields cannot be in fragments.
 */
class AppFeatureDirectiveLink extends ApolloLink {
  constructor(appFeatures) {
    super();
    this._appFeatures = appFeatures;
    this.cachedQueries = new Map();
  }

  get appFeatures() {
    return this._appFeatures;
  }

  set appFeatures(newAppFeatures) {
    // Only reset the cache if it is a new object
    if (this._appFeatures !== newAppFeatures) {
      this._appFeatures = newAppFeatures;
      this.cachedQueries = new Map();
    }
  }

  /**
   * Returns details about the appFeature directive found for the given node
   */
  getAppFeatureDirectiveInfo(node) {
    const directive = node.directives?.find(
      d => d?.name?.value === 'appFeature'
    );
    const feature = directive?.arguments.find(a => a.name?.value === 'feature')
      ?.value?.value;

    return directive && feature ? { directive, feature } : null;
  }

  /**
   * This function is a bit complex and returns two things :( Processing queries
   * can be expensive, so returning two values here saves some extra processing
   * of the query document. It also removes the directives. All these responsibilities
   * are kept in here for ease of caching.
   *
   * Returns:
   * updatedQuery - query that has fields that are feature flagged off removed
   * removedFields - is an array of objects representing removed fields
   */
  removeFieldsFromDocument(originalQuery) {
    // This function can be expensive so cache it, Apollo uses the query object
    // as the cache key so this should be fine here as well.
    if (this.cachedQueries.has(originalQuery)) {
      return this.cachedQueries.get(originalQuery);
    }
    const removedFields = [];
    const location = [];
    const queryWithoutDisabledFields = visit(originalQuery, {
      Field: {
        enter: node => {
          location.push(node.alias?.name || node.name.value);
          const directive = this.getAppFeatureDirectiveInfo(node);
          if (directive && !this.appFeatures[directive.feature]) {
            LOG.debug(
              `Preventing field ${location.join(
                '/'
              )} from being sent to the server`
            );
            removedFields.push({ location: [...location], ...directive });
            // return null will remove this field from the query, and will not
            // call leave so we need to pop the location before returning
            location.pop();
            return null;
          }
          return undefined;
        },
        leave: _ => {
          location.pop();
          return undefined;
        }
      }
    });

    checkDocument(queryWithoutDisabledFields);
    const docClone = removeDirectivesFromDocument(
      [{ name: 'appFeature' }],
      queryWithoutDisabledFields
    );
    const newQuery = gql(print(docClone));

    // Cache the response so we don't have to do all this processing next time
    this.cachedQueries.set(originalQuery, [newQuery, removedFields]);
    return this.cachedQueries.get(originalQuery);
  }

  /**
   * This adds results for the stripped fields to the response, currently only
   * null is supported.
   */
  injectFieldsIntoResult(removedFields, result) {
    for (const { location } of removedFields) {
      LOG.debug(
        `Injecting query result for supressed field ${location.join('/')}`
      );
      setField(result.data, location, null);
    }
    return result;
  }

  request(operation, forward) {
    const { query: originalQuery } = operation;

    const hasAppFeatureDirectives = hasDirectives(
      ['appFeature'],
      originalQuery
    );

    if (hasAppFeatureDirectives) {
      const [updatedQuery, removedFields] =
        this.removeFieldsFromDocument(originalQuery);
      // eslint-disable-next-line no-param-reassign
      operation.query = updatedQuery;

      if (removedFields.length > 0) {
        return forward(operation).map(result =>
          this.injectFieldsIntoResult(removedFields, result)
        );
      }
    }

    return forward(operation);
  }
}

export default AppFeatureDirectiveLink;
